import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'Text to Speech iOS';
const heroDescription =
  'Text to speech iOS app for academic content. Perfect pronunciation of scientific terms, intelligent document processing. Download for iPhone & iPad.';

const featureTitle =
  'Not another Voice Dream Reader or Natural Reader - the iOS academic audio solution that helps:';
const featureSubtitle = '';
const featureDescription = '';
const featureBadge = 'iOS Exclusive';
const featureVariant = 'default';
const featureCtaText = 'Download for iOS';

const featureFeatures = [
  {
    title: 'iPhone Study Sessions',
    description:
      'iPhone-using students convert textbooks and papers into study sessions during commutes',
  },
  {
    title: 'iPad Research Processing',
    description:
      'iPad researchers process academic literature with perfect scientific pronunciation',
  },
  {
    title: 'Specialized iOS Features',
    description:
      'iOS academics access specialized features unavailable in generic App Store TTS apps',
  },
  {
    title: 'Mobile Academic Time',
    description:
      'Mobile learners transform any iPhone moment into productive academic time',
  },
];

const solutionBenefitsData = {
  title: 'Feature Bullets',
  items: [
    {
      text: '95% accuracy on scientific terminology with iOS-optimized pronunciation',
      highlight: '95% accuracy',
    },
    {
      text: 'Seamless AirPods and CarPlay integration for hands-free academic listening',
      highlight: 'AirPods and CarPlay',
    },
    {
      text: 'Offline processing - study anywhere without internet connection',
      highlight: 'Offline processing',
    },
    {
      text: 'Background audio continues while using other iPhone apps',
      highlight: 'Background audio',
    },
  ],
};

const useCasesData = {
  title: 'Use Case Scenarios',
  description:
    'Real-world scenarios where iOS text-to-speech transforms academic productivity on iPhone and iPad.',
  useCases: [
    {
      title: 'Medical Student on Clinical Rotations',
      challenge: 'Study medical literature during hospital breaks using iPhone',
      solutionSections: [
        {
          label: 'iOS TTS Solution',
          content:
            'Perfect pronunciation of drug names and medical procedures through AirPods',
        },
        {
          label: 'Generic App Problem',
          content:
            'Generic App Store TTS apps mispronounce medical terms, reducing effectiveness',
        },
      ],
    },
    {
      title: 'Graduate Student Commuter',
      challenge: '90-minute daily train commute could be productive study time',
      solutionSections: [
        {
          label: 'iOS TTS Solution',
          content:
            'Listen to thesis research papers with automatic section navigation',
        },
        {
          label: 'Generic App Problem',
          content:
            'Citation disruptions and poor academic structure recognition',
        },
      ],
    },
    {
      title: 'International PhD Student',
      challenge:
        'Process English research papers while improving pronunciation on iPhone',
      solutionSections: [
        {
          label: 'iOS TTS Solution',
          content:
            'Clear academic pronunciation helps with both comprehension and language learning',
        },
        {
          label: 'Generic App Problem',
          content:
            "Generic pronunciation doesn't help with scientific communication skills",
        },
      ],
    },
    {
      title: 'Busy Academic Professional',
      challenge:
        'Stay current with field literature using only iPhone during busy schedule',
      solutionSections: [
        {
          label: 'iOS TTS Solution',
          content:
            'Background audio while exercising and automatic sync across devices',
        },
        {
          label: 'Generic App Problem',
          content: 'Apps pause when switching between iPhone applications',
        },
      ],
    },
  ],
};

const comparisonTableData = {
  title: 'Comparison Table',
  description:
    'Compare iOS text-to-speech apps and their effectiveness for academic content on iPhone and iPad.',
  columns: [
    {
      header: 'Academic TTS iOS',
      subheader: 'Listening',
      isHighlighted: true,
    },
    { header: 'Voice Dream Reader', isHighlighted: false },
    { header: 'Natural Reader', isHighlighted: false },
    { header: 'iOS Accessibility', isHighlighted: false },
  ],
  rows: [
    {
      feature: 'Scientific Terms',
      description:
        'Accurate pronunciation of scientific and technical terminology.',
      values: [
        { text: '95% accuracy, academic training', icon: 'check' as const },
        { text: '80% accuracy, general vocabulary', icon: 'warning' as const },
        { text: '75% accuracy, basic support', icon: 'warning' as const },
        { text: '85% accuracy, no specialization', icon: 'warning' as const },
      ],
    },
    {
      feature: 'Academic Structure',
      description:
        'Recognition and handling of academic document formatting and citations.',
      values: [
        {
          text: 'Recognizes paper sections, skips citations',
          icon: 'check' as const,
        },
        { text: 'Linear reading only', icon: 'close' as const },
        { text: 'No academic awareness', icon: 'close' as const },
        { text: 'Basic text processing', icon: 'close' as const },
      ],
    },
    {
      feature: 'iOS Integration',
      description:
        'Integration with iOS-specific features like Siri, AirPods, and CarPlay.',
      values: [
        { text: 'Full Siri, AirPods, CarPlay support', icon: 'check' as const },
        { text: 'Basic iOS integration', icon: 'warning' as const },
        { text: 'Limited iOS features', icon: 'warning' as const },
        { text: 'Native accessibility features', icon: 'check' as const },
      ],
    },
    {
      feature: 'Offline Capability',
      description:
        'Ability to process documents without internet connection on iOS devices.',
      values: [
        { text: 'Full offline processing', icon: 'check' as const },
        { text: 'Offline available', icon: 'check' as const },
        { text: 'Requires internet connection', icon: 'close' as const },
        { text: 'Offline accessibility', icon: 'check' as const },
      ],
    },
    {
      feature: 'Document Formats',
      description:
        'Support for various academic document formats on iOS devices.',
      values: [
        { text: 'PDF, Word, EPUB, academic papers', icon: 'check' as const },
        { text: 'Multiple formats supported', icon: 'check' as const },
        { text: 'Limited format support', icon: 'warning' as const },
        { text: 'Text selection only', icon: 'close' as const },
      ],
    },
    {
      feature: 'Academic Pricing',
      description:
        'Pricing structure designed for students and academic users.',
      values: [
        { text: 'Student-friendly iOS app pricing', icon: 'check' as const },
        { text: '$90/year expensive for students', icon: 'close' as const },
        { text: 'Premium subscription required', icon: 'close' as const },
        { text: 'Free with iOS', icon: 'check' as const },
      ],
    },
  ],
  showIcons: true,
  ariaLabel: 'iOS Text-to-Speech Academic Apps Comparison Table',
};

const seoSections = [
  {
    title: 'Why Academic iOS Users Need Specialized Text-to-Speech',
    content:
      "While the App Store offers dozens of text-to-speech apps, most iOS TTS solutions are designed for casual content, not academic work. Generic iPhone apps like Voice Dream Reader and Natural Reader struggle with the unique requirements of scholarly material: scientific terminology pronunciation, citation management, and academic document structure recognition. When you're using your iPhone to study complex academic papers on your iPad, these limitations become critical barriers to effective learning.",
    contentSecondary:
      "Academic iOS users face unique challenges that consumer TTS apps don't address. Research papers contain Latin-derived terminology, complex mathematical arguments, and structured arguments that require specialized audio processing. Our academic text-to-speech iOS app solves these problems with 95% accuracy on scientific terms compared to 78% average of generic iOS TTS apps, intelligent citation skipping that eliminates disruptive reference numbers, and automatic section organization that lets you navigate between Abstract, Methods, Results, and Discussion with seamless iOS integration.",
  },
  {
    title: 'Maximize Academic Productivity on iPhone and iPad',
    content:
      'Our iOS text-to-speech app transforms your iPhone into a powerful academic tool that processes any document format - PDFs, Word files, research papers, and textbooks - with professional-quality narration optimized for educational content. Unlike basic iOS accessibility features that read everything linearly, our app understands academic content structure and provides intelligent audio processing that maintains comprehension flow during mobile learning sessions.',
    contentSecondary:
      'The iOS-specific features include seamless AirPods integration for hands-free academic listening, background processing that continues reading while you use other iPhone apps, and offline capability that doesn\'t require internet connection for study anywhere. Users consistently report transforming previously "dead time" - commuting, exercising, walking between classes - into productive academic sessions, with testimonials noting "finally found an iOS app that handles scientific papers properly" and "perfect for medical study during clinical rotations."',
  },
];

export const Route = createFileRoute('/_marketing/_seo/text-to-speech-ios')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Text to Speech iOS App - Academic TTS for iPhone & iPad | Listening',
        description:
          'Text to speech iOS app for academic content. Perfect pronunciation of scientific terms, intelligent document processing. Download for iPhone & iPad.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
